import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Sparkles, MessageSquare, ChevronDown, ChevronUp, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

/**
 * "Ask the Boardroom" — cross-session grounded Q&A.
 *
 * Renders a compact input on the dashboard that lets the user interrogate
 * every past session at once. Answers cite specific sessions with clickable
 * links. History is persisted per-user (last 20 shown).
 *
 * Prop: `hasTranscripts` (bool) — when false we render an inert coaching
 * card instead of the live input, so a brand-new user isn't nudged into a
 * feature they can't use yet.
 */
/** The archivist writes in light markdown; render the bold and drop the rest. */
const AnswerText = ({ text, className, testId }) => {
  const clean = String(text || "")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/^\s*[-–—_*]{3,}\s*$/gm, "")
    .replace(/^\s*[-*]\s+/gm, "· ")
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$|[.,;:!?])/g, "$1$2")
    .trim();
  return (
    <p className={className} data-testid={testId}>
      {clean.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="text-[#eaeaea] font-semibold">{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      )}
    </p>
  );
};

export default function AskBoardroom({ hasRecord }) {
  const [history, setHistory] = useState([]);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const historyLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasRecord || historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    api.get("/boardroom/ask/history").then((r) => {
      setHistory(r.data.history || []);
    }).catch((e) => {
      console.debug("boardroom history load failed", e);
    });
  }, [hasRecord]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (!q || pending) return;
    setPending(true);
    try {
      const res = await api.post("/boardroom/ask", { question: q });
      // Re-fetch from server so multi-tab / multi-device users stay in sync
      // with the 50-entry cap + chronological order (rather than optimistically
      // appending, which can miss entries added elsewhere).
      try {
        const h = await api.get("/boardroom/ask/history");
        setHistory(h.data.history || []);
      } catch (_) {
        // Fallback to optimistic append if the history fetch fails.
        setHistory((prev) => [...prev, res.data].slice(-20));
      }
      setQuestion("");
      setExpanded(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("Session no longer available.");
      } else if (detail?.code === "no_transcripts" || detail?.code === "no_record") {
        toast.error(detail.message || "Hold a session first — then the archive has something to answer from.");
      } else if (detail?.code === "rate_limited") {
        toast.error(detail.message || "Slow down a bit — try again shortly.");
      } else if (detail?.code === "question_too_long") {
        toast.error("Keep questions under 500 characters.");
      } else if (detail?.code === "ai_upstream_unavailable") {
        toast.error("AI is temporarily unavailable. Try again in a moment.");
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Ask failed");
      }
    } finally {
      setPending(false);
    }
  };

  // Empty-state variant — invite them to hold their first session.
  if (!hasRecord) {
    return (
      <div className="surface rounded-md p-6 border-[#c89f45]/20" data-testid="ask-boardroom-empty">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#c89f45]" />
          <p className="label-eyebrow">Compounding memory</p>
        </div>
        <h3 className="font-serif text-xl mt-2">Ask the Boardroom</h3>
        <p className="text-sm text-[#888] mt-2 max-w-2xl">
          Hold your first session and this box becomes a searchable record of every commitment
          you've made — notes, minutes and transcripts all count. Ask <em className="text-[#bbb]">"What have I
          been putting off for three weeks?"</em> and it'll answer, citing the exact session.
        </p>
      </div>
    );
  }

  const latest = history.length > 0 ? history[history.length - 1] : null;
  const older = history.length > 1 ? history.slice(0, -1) : [];

  return (
    <div className="surface rounded-md p-6" data-testid="ask-boardroom-block">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#c89f45]" />
        <p className="label-eyebrow">Compounding memory</p>
      </div>
      <h3 className="font-serif text-xl sm:text-2xl mt-2">Ask the Boardroom</h3>
      <p className="text-sm text-[#888] mt-2 max-w-2xl">
        Interrogate every past session at once. Answers are grounded in your own record — notes,
        minutes and transcripts — and cite the sessions they came from.
      </p>

      <form onSubmit={submit} className="mt-5 flex items-center gap-2" data-testid="ask-boardroom-form">
        <div className="flex-1 relative">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={500}
            placeholder="What have I been putting off for more than 3 weeks?"
            disabled={pending}
            className="w-full h-11 bg-[#121212] border border-[#2a2a2a] focus:border-[#c89f45] outline-none rounded-md px-4 pr-16 text-sm text-[#fafafa] placeholder:text-[#8a8a8a] transition-colors"
            data-testid="ask-boardroom-input"
          />
          {question.length > 0 && (
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono ${
                question.length >= 480 ? "text-[#e07b6a]" : "text-[#8a8a8a]"
              }`}
              data-testid="ask-boardroom-charcount"
            >
              {question.length}/500
            </span>
          )}
        </div>
        <Button
          type="submit"
          disabled={pending || !question.trim()}
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-11 px-4"
          data-testid="ask-boardroom-submit"
        >
          <Send size={14} className="mr-1.5" />
          {pending ? "Asking…" : "Ask"}
        </Button>
      </form>

      {latest && (
        <div className="mt-6 border-l-2 border-[#c89f45]/50 pl-4" data-testid="ask-boardroom-latest">
          <p className="text-sm text-[#ddd] font-medium flex items-start gap-2">
            <MessageSquare size={12} className="text-[#c89f45] mt-1 shrink-0" />
            <span>{latest.question}</span>
          </p>
          <AnswerText
            text={latest.answer}
            className="text-sm text-[#bbb] mt-2 leading-relaxed whitespace-pre-wrap"
            testId="ask-boardroom-latest-answer"
          />
          {Array.isArray(latest.sessions_referenced) && latest.sessions_referenced.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-boardroom-refs">
              {latest.sessions_referenced.slice(0, 6).map((s) => (
                <Link
                  key={s.id}
                  to={`/meetings/${s.id}/minutes`}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#c89f45]/10 text-[#888] hover:text-[#c89f45] transition-colors inline-flex items-center gap-1"
                  data-testid={`ask-boardroom-ref-${s.id}`}
                >
                  {s.title.slice(0, 32)}{s.title.length > 32 ? "…" : ""} · {s.date}
                  <ArrowUpRight size={10} />
                </Link>
              ))}
              {latest.sessions_referenced.length > 6 && (
                <span
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#1a1a1a] text-[#8a8a8a]"
                  title={latest.sessions_referenced.slice(6).map((s) => `${s.title} · ${s.date}`).join("\n")}
                  data-testid="ask-boardroom-refs-more"
                >
                  +{latest.sessions_referenced.length - 6} more
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {older.length > 0 && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] hover:text-[#c89f45] flex items-center gap-1 transition-colors"
            data-testid="ask-boardroom-history-toggle"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Hide" : "Show"} {older.length} earlier question{older.length === 1 ? "" : "s"}
          </button>
          {expanded && (
            <ul className="mt-4 space-y-4" data-testid="ask-boardroom-older">
              {older.slice().reverse().map((entry) => (
                <li key={entry.id} className="border-l border-[#2a2a2a] pl-4">
                  <p className="text-xs text-[#888] flex items-start gap-2">
                    <MessageSquare size={10} className="text-[#8a8a8a] mt-0.5 shrink-0" />
                    <span>{entry.question}</span>
                  </p>
                  <AnswerText text={entry.answer} className="text-xs text-[#999] mt-1 leading-relaxed whitespace-pre-wrap" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
